import { createRoot, createPortal } from '@wordpress/element';
import { differenceInCalendarDays } from 'date-fns';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LazyMotion } from 'motion/react';
import { useEffect } from 'react';
import type { StoreApi } from 'zustand';
import GPBField from './field';
import { BookingTimePicker } from './components/BookingTimePicker';
import { useDOMFieldSync } from './hooks/useDOMFieldSync';
import { useResourceFieldUpdater } from './hooks/useResourceFieldUpdater';
import {
	createBookingStore,
	type BookingStore,
} from './store/createBookingStore';
import {
	BookingStoreProvider,
	useBookingStore,
} from './contexts/BookingStoreContext';
import { TimezoneProvider } from '../shared/contexts/TimezoneContext';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useConditionalLogic } from './hooks/useConditionalLogic';
import { fieldQueryClientFactory } from './lib/queryClient';

interface GPBFieldBookingArgs {
	formId: number;
	fieldId: number;
	bookingFieldId: number;
	serviceId: number;
	serviceFieldId: number | null;
	resourceFieldIds: number[];
	nonce: string;
	slotMode: 'aligned' | 'flexible';
	interval: number;
	blockSize?: number;
	startingMonthMode?: 'first_availability_window' | 'current' | 'specific';
	startingMonth?: number | null;
	startingYear?: number | null;
	showEndTimes?: boolean;
	showTimezoneIndicator?: boolean;
	timezoneMessage?: string | null;
	showSpotsLeft?: boolean;
	spotsLeftText?: string | null;
	excludeBookingId?: number | null;
}

/**
 * Unified booking field component that coordinates all booking interactions.
 *
 * This component:
 * - Monitors service field changes (manual or preselected mode)
 * - Updates resource field options when service changes
 * - Renders the booking time picker with proper context
 * - Handles merge tag registration for duration calculations
 */
class GPBFieldBooking extends GPBField {
	public bookingFieldId: number;
	public serviceId: number;
	public serviceFieldId: number | null;
	public resourceFieldIds: number[];
	public slotMode: 'aligned' | 'flexible';
	public interval: number;
	public blockSize: number = 0;
	public startingMonthMode:
		| 'first_availability_window'
		| 'current'
		| 'specific';
	public startingMonth: number | null;
	public startingYear: number | null;
	public showEndTimes: boolean;
	public showTimezoneIndicator: boolean;
	public timezoneMessage: string | null;
	public showSpotsLeft: boolean;
	public spotsLeftText: string | null;
	public excludeBookingId: number | null;
	private root: ReturnType<typeof createRoot> | null = null;
	public queryClient: QueryClient;
	public store: StoreApi<BookingStore>;

	public constructor(args: GPBFieldBookingArgs) {
		super(args);

		this.bookingFieldId = args.bookingFieldId;
		this.serviceId = args.serviceId;
		this.serviceFieldId = args.serviceFieldId;
		this.resourceFieldIds = args.resourceFieldIds || [];
		this.slotMode = args.slotMode;
		this.interval = args.interval;
		this.blockSize = args.blockSize ?? 0;
		this.startingMonthMode =
			args.startingMonthMode ?? 'first_availability_window';
		this.startingMonth = args.startingMonth ?? null;
		this.startingYear = args.startingYear ?? null;
		this.showEndTimes = args.showEndTimes ?? true;
		this.showTimezoneIndicator = args.showTimezoneIndicator ?? true;
		this.timezoneMessage = args.timezoneMessage ?? null;
		this.showSpotsLeft = args.showSpotsLeft ?? false;
		this.spotsLeftText = args.spotsLeftText ?? null;
		this.excludeBookingId = args.excludeBookingId ?? null;

		// Create per-field QueryClient and Zustand store for isolation
		this.queryClient = fieldQueryClientFactory();
		this.store = createBookingStore('field-' + this.bookingFieldId);

		this.init();
	}

	public init() {
		const container = this.$field.find('.gpb-booking-time-picker')[0];
		if (!container) {
			// eslint-disable-next-line no-console
			console.error('Booking time picker container not found');
			return;
		}

		const input = this.$field.find(`#input_${this.formId}_${this.fieldId}`);
		if (!input.length) {
			// eslint-disable-next-line no-console
			console.error('Booking time input not found');
			return;
		}

		this.root = createRoot(container);
		this.root.render(
			<BookingField
				field={this}
				value={input.val() as string}
				onChange={(value) => {
					input.val(value);
					input.trigger('change');
				}}
			/>
		);

		// Register per-field merge tag filter for duration modifiers
		this.registerMergeTagFilter();
	}

	private registerMergeTagFilter() {
		const gformGlobal = (window as any).gform;
		if (!gformGlobal || typeof gformGlobal.addFilter !== 'function') {
			return;
		}

		const formId = this.formId;
		const fieldId = this.fieldId;
		const blockSize = this.blockSize;

		const MILLISECONDS_PER_MINUTE = 60000;
		const MINUTES_PER_HOUR = 60;

		function parseDate(dateString: string): Date | null {
			if (!dateString) {
				return null;
			}
			const dateObj = new Date(dateString);
			return isNaN(dateObj.getTime()) ? null : dateObj;
		}

		gformGlobal.addFilter(
			'gform_value_merge_tag_' + formId + '_' + fieldId,
			function (value: any, _input: any, modifier?: string) {
				if (!modifier) {
					return false;
				}

				modifier = ('' + modifier).toLowerCase();

				// Skip date-specific modifiers
				if (
					modifier.startsWith('startdate') ||
					modifier.startsWith('enddate')
				) {
					return '';
				}

				if (!modifier.startsWith('duration[')) {
					return false;
				}

				const inputEl = document.getElementById(
					'input_' + formId + '_' + fieldId
				) as HTMLInputElement | null;
				const dateRangeValue =
					inputEl && inputEl.value ? inputEl.value : '';

				if (!dateRangeValue) {
					return '0';
				}

				const parts = dateRangeValue.split('|');
				const startDate = parseDate(parts[0]);
				let endDate: Date | null =
					parts.length > 1 ? parseDate(parts[1]) : null;

				if (!endDate && startDate && blockSize > 0) {
					endDate = new Date(
						startDate.getTime() +
							blockSize * MILLISECONDS_PER_MINUTE
					);
				}

				if (!startDate || !endDate) {
					return '0';
				}

				const durationMinutes = Math.max(
					0,
					Math.floor(
						(endDate.getTime() - startDate.getTime()) /
							MILLISECONDS_PER_MINUTE
					)
				);
				const unit = modifier.slice('duration['.length, -1);

				switch (unit) {
					case 'minutes':
						return String(durationMinutes);
					case 'hours':
						return String(
							Math.floor(durationMinutes / MINUTES_PER_HOUR)
						);
					case 'nights':
						return String(
							differenceInCalendarDays(endDate, startDate)
						);
					case 'days':
						return String(
							differenceInCalendarDays(endDate, startDate) + 1
						);
					default:
						return '0';
				}
			}
		);
	}

	public destroy() {
		if (this.root) {
			this.root.unmount();
			this.root = null;
		}
	}
}

/**
 * Inner component that uses React Query hooks (must be inside QueryClientProvider)
 */
function BookingFieldInner({
	field,
	value,
	onChange,
}: {
	field: GPBFieldBooking;
	value: string;
	onChange: (value: string) => void;
}) {
	const {
		formId,
		bookingFieldId,
		serviceId: staticServiceId,
		serviceFieldId,
		resourceFieldIds,
		slotMode,
		interval,
		showEndTimes,
		showTimezoneIndicator,
		timezoneMessage,
		showSpotsLeft,
		spotsLeftText,
		startingMonthMode,
		startingMonth,
		startingYear,
		excludeBookingId,
	} = field;

	const setConfig = useBookingStore((state) => state.setConfig);
	const setExcludeBookingId = useBookingStore(
		(state) => state.setExcludeBookingId
	);

	// Sync all uncontrolled DOM fields to store
	useDOMFieldSync({
		formId,
		bookingFieldId,
		serviceFieldId,
		fallbackServiceId: staticServiceId,
		resourceFieldIds,
	});

	// Update resource field options when service changes (reads from store)
	useResourceFieldUpdater();

	// Initialize store config once
	useEffect(() => {
		setConfig({
			formId,
			bookingFieldId,
			serviceFieldId,
			resourceFieldIds,
			slotMode,
			interval,
			showEndTimes,
			showTimezoneIndicator,
			timezoneMessage,
			showSpotsLeft,
			spotsLeftText,
			startingMonthMode,
			startingMonth,
			startingYear,
			excludeBookingId,
		});
	}, [
		formId,
		bookingFieldId,
		serviceFieldId,
		resourceFieldIds,
		slotMode,
		interval,
		showEndTimes,
		showTimezoneIndicator,
		timezoneMessage,
		showSpotsLeft,
		spotsLeftText,
		startingMonthMode,
		startingMonth,
		startingYear,
		setConfig,
		excludeBookingId,
	]);

	// Set excludeBookingId separately when in entry edit mode
	useEffect(() => {
		if (excludeBookingId) {
			setExcludeBookingId(excludeBookingId);
		}
	}, [excludeBookingId, setExcludeBookingId]);

	return (
		<LazyMotion
			features={() =>
				import('./lib/motion-features').then((res) => res.domAnimation)
			}
		>
			<TimezoneProvider>
				<BookingTimePicker value={value} onChange={onChange} />
			</TimezoneProvider>
		</LazyMotion>
	);
}

/**
 * Outer component that provides QueryClient and Store context
 */
function BookingField({
	field,
	value,
	onChange,
}: {
	field: GPBFieldBooking;
	value: string;
	onChange: (value: string) => void;
}) {
	// Track resource field visibility
	const fieldVisibility = useConditionalLogic({
		formId: field.formId,
		fieldId: field.bookingFieldId,
	});

	return (
		<QueryClientProvider client={field.queryClient}>
			<BookingStoreProvider store={field.store}>
				<BookingFieldInner
					field={field}
					value={value}
					onChange={onChange}
				/>
			</BookingStoreProvider>
			{fieldVisibility &&
				createPortal(
					<ReactQueryDevtools initialIsOpen={false} />,
					document.body
				)}
		</QueryClientProvider>
	);
}

export default GPBFieldBooking;

window.GPBFieldBooking = GPBFieldBooking;
